import { ACK_STATUS, RCU_STATUS_TYPE } from '../../lib/rcu/catalog/ack';
import { EVENT_DIRECTION } from '../../lib/rcu/catalog/link';
import { REPLY_VERDICT } from '../../features/keypads';
import type { KeypadReply } from '../../features/keypads';
import type { RcuEvent } from '../../lib/rcu/link/events';

/**
 * The device's last word about a mapping, as the tab hands it to the feature.
 *
 * The link has already filed each message well enough to know what it is, and
 * that one fact is all this reads: whether an acknowledgement said ok or fail, or
 * whether a read answered. The payload travels through untouched, so what the
 * panel shows is the firmware's own text rather than this console's summary of
 * it — which is the only way to see a reply that no model here covers.
 */

/** Every acknowledgement that concerns a mapping rather than a live channel. */
const REPLY_ACK_TYPES = [
  RCU_STATUS_TYPE.MAPPING_ACK,
  RCU_STATUS_TYPE.MAPPING_ERASE,
  RCU_STATUS_TYPE.MAPPING_ERASE_ALL,
  RCU_STATUS_TYPE.LED_MAPPING_ACK,
  RCU_STATUS_TYPE.LED_MAPPING_ERASE,
  RCU_STATUS_TYPE.S3_MAPPING_ACK,
] as const;

const isMappingAck = (statusType: string): boolean =>
  REPLY_ACK_TYPES.some((candidate) => candidate === statusType);

/** One inbound event as a reply, or null when it says nothing about mappings. */
export const keypadReplyOf = (event: RcuEvent): KeypadReply | null => {
  if (event.direction !== EVENT_DIRECTION.IN) return null;
  const reading = event.reading;

  if (reading.kind === 'mappingRead' || reading.kind === 'ledMappingRead') {
    return { at: event.at, topic: event.topic, text: event.raw, verdict: REPLY_VERDICT.READ };
  }

  if (reading.kind !== 'commandResult' || !isMappingAck(reading.statusType)) return null;
  return {
    at: event.at,
    topic: event.topic,
    text: event.raw,
    verdict:
      reading.status === ACK_STATUS.FAIL ? REPLY_VERDICT.REFUSED : REPLY_VERDICT.ACCEPTED,
  };
};

/** The newest reply in the feed, or null when the device has not answered yet. */
export const lastKeypadReply = (events: readonly RcuEvent[]): KeypadReply | null => {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (event === undefined) continue;
    const reply = keypadReplyOf(event);
    if (reply !== null) return reply;
  }
  return null;
};
